// Remembers fields the user typed into. KeepAliveTab keeps tabs mounted and a
// tab tap blurs a half-typed inline field, so focus alone cannot tell the
// silent app-update gate that text is waiting there. Search fields are exempt
// (throwaway text) and password fields are never held; an element counts only
// while connected AND non-empty, and detached ones are dropped on every input.

const NON_TEXT_INPUT_TYPES = new Set(['checkbox', 'radio', 'button', 'submit', 'range', 'file', 'hidden']);

export function isEditableElement(el) {
  if (!el || el.nodeType !== 1) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return !NON_TEXT_INPUT_TYPES.has((el.getAttribute('type') || 'text').toLowerCase());
  return el.isContentEditable === true || el.getAttribute('contenteditable') === 'true' || el.getAttribute('contenteditable') === '';
}

export function isSearchField(el) {
  if (!el || el.nodeType !== 1) return false;
  if ((el.getAttribute('type') || '').toLowerCase() === 'search') return true;
  if (el.getAttribute('role') === 'searchbox') return true;
  return Boolean(el.closest && el.closest('[role="search"]'));
}

function isPasswordField(el) {
  return Boolean(el && el.tagName === 'INPUT' && (el.getAttribute('type') || '').toLowerCase() === 'password');
}

const typed = new Set();
const installedDocs = new WeakSet();

export function installTypingTracker(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || installedDocs.has(doc)) return;
  installedDocs.add(doc);
  doc.addEventListener('input', (e) => {
    const el = e.target;
    for (const prev of Array.from(typed)) {
      if (!prev.isConnected) typed.delete(prev);
    }
    if (isPasswordField(el)) return; // never hold a password field
    if (isEditableElement(el) && !isSearchField(el)) typed.add(el);
  }, true);
  // A submitted form has handed its text over — forget its fields.
  doc.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || typeof form.contains !== 'function') return;
    for (const el of Array.from(typed)) {
      if (form.contains(el)) typed.delete(el);
    }
  }, true);
}

const hasContent = (el) => {
  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
    return String(el.value ?? '').trim() !== '';
  }
  return String(el.textContent ?? '').trim() !== '';
};

export function hasLiveTypedField() {
  let live = false;
  for (const el of Array.from(typed)) {
    if (!el.isConnected || !hasContent(el)) { typed.delete(el); continue; }
    live = true;
  }
  return live;
}

export function __resetTypingTrackerForTests() {
  typed.clear();
}
